/* Upload a skill: drop a folder, a .zip / .skill file or one SKILL.md; the server's
   checker says what it found and every problem, pinned to its file, before anything is
   stored; Save stores it (tasks/custom-skills/01-design-mock.md). */
import React, { useRef, useState } from "react";
import { FolderUp, Loader2, ShieldQuestion, SquareTerminal, Upload } from "lucide-react";
import { toast } from "sonner";

import { Counted, ListCard } from "@/shared/components/ListCard";
import { FileTypeIcon } from "@/shared/components/icons/filetype";
import { Breadcrumb } from "@/shared/components/Breadcrumb";
import { BrandButton, Chip, GhostButton, PageHeader } from "@/shared/components/ui-kit";
import { extension } from "@/shared/components/viewer/model";

import { buildTree, formatBytes } from "../catalog";
import { folderForm, fromDrop, fromInput, isKept, problemsOf, tooBigToSend } from "../folder";
import { useCheckSkill, useCreateSkill } from "../hooks/useSkillWrites";
import { ProblemLines } from "./atoms";
import SkillMarkdown from "./SkillMarkdown";

import type { Picked } from "../folder";
import type { ApiError } from "@/shared/api/errors";
import type { SkillCheck, SkillFile, SkillProblem } from "@/shared/types";

/** What was picked, as a name for the "Checking …" line. */
const pickedName = (picked: Picked[]) =>
  picked.length === 1 ? picked[0].path : picked[0].path.split("/")[0] || `${picked.length} files`;

export default function UploadSkill({ onBack, onSaved }: {
  onBack: () => void;
  onSaved: (skillId: string) => void;
}) {
  const [picked, setPicked] = useState<Picked[] | null>(null);
  const [tooBig, setTooBig] = useState(false);
  const check = useCheckSkill();
  const create = useCreateSkill();

  const take = (all: Picked[]) => {
    // what no skill keeps (.git, .DS_Store …) never goes up: a repo's history alone can be MBs
    const files = all.filter((f) => isKept(f.path));
    if (!files.length) return;
    setPicked(files);
    create.reset();
    const big = tooBigToSend(files.map((f) => f.body));
    setTooBig(big);
    if (!big) check.mutate(folderForm(files));
  };
  const again = () => {
    setPicked(null);
    setTooBig(false);
    check.reset();
    create.reset();
  };
  const save = () =>
    picked && create.mutate(folderForm(picked), {
      onSuccess: (skill) => {
        toast.success(`${skill.name} saved to the workspace`, { description: "Put it on an agent from the agent's Settings." });
        onSaved(skill.id);
      },
    });

  // A save the server refused after the check said yes (a name taken meanwhile): its problems win
  const found: SkillCheck | undefined = check.data && (create.isError
    ? { ...check.data, problems: problemsOf(create.error as ApiError) }
    : check.data);

  return (
    <>
      <PageHeader title={<Breadcrumb parent={{ label: "Skills", onClick: onBack }}>Upload a skill</Breadcrumb>} />
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="max-w-[880px] mx-auto px-4 md:px-6 py-6 space-y-4 fade-in-up">
          {!picked && <DropArea onPick={take} />}
          {picked && tooBig && (
            <div role="alert" className="tray-box py-10 flex flex-col items-center gap-3 text-center">
              <div className="text-[13px] text-[var(--text-secondary)] max-w-[440px]">
                Over 10 MB, more than one upload can send. Zip the folder and upload the .zip: it can unpack to 20 MB.
              </div>
              <GhostButton onClick={again}>Choose another</GhostButton>
            </div>
          )}
          {picked && check.isPending && (
            <div role="status" className="tray-box flex flex-col items-center justify-center gap-2 py-14 text-center">
              <Loader2 className="w-[18px] h-[18px] animate-spin text-[var(--text-tertiary)]" strokeWidth={1.75} />
              <div className="text-[13px] shimmer-text">Checking {pickedName(picked)}</div>
            </div>
          )}
          {picked && check.isError && (
            <div role="alert" className="tray-box py-10 flex flex-col items-center gap-3 text-center">
              <div className="text-[13px] text-[var(--text-secondary)]">Couldn't check it: {(check.error as ApiError).message}</div>
              <div className="flex gap-2">
                <GhostButton onClick={again}>Choose another</GhostButton>
                <BrandButton onClick={() => take(picked)}>Try again</BrandButton>
              </div>
            </div>
          )}
          {found && <Found check={found} />}
          {found && (
            <div className="flex items-center justify-end gap-2 pt-1">
              <GhostButton onClick={again} disabled={create.isPending}>Choose another</GhostButton>
              <BrandButton
                onClick={save}
                disabled={found.problems.length > 0 || create.isPending}
                title={found.problems.length ? "Fix the problems first, then upload it again" : undefined}
                className="disabled:opacity-40 disabled:pointer-events-none"
              >
                {create.isPending && <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.25} />}
                Save skill
              </BrandButton>
            </div>
          )}
          {create.isError && !problemsOf(create.error as ApiError).length && (
            <p role="alert" className="m-0 text-right text-[12.5px] text-[var(--danger)]">{(create.error as ApiError).message}</p>
          )}
        </div>
      </div>
    </>
  );
}

/** The drop target and the two pickers: a folder keeps its paths, a file is a zip or one SKILL.md. */
function DropArea({ onPick }: { onPick: (files: Picked[]) => void }) {
  const [over, setOver] = useState(false);
  const folderInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const picked = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) onPick(fromInput(e.target.files));
    e.target.value = ""; // the same pick again still fires
  };
  return (
    <>
      <div
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          fromDrop(e.dataTransfer).then(onPick, (error: unknown) => toast.error(`Couldn't read what was dropped: ${error instanceof Error ? error.message : String(error)}`));
        }}
        className="rounded-[18px] bg-[var(--surface-2)] p-1"
      >
        <div className={`rounded-[14px] border-[1.5px] border-dashed bg-[var(--surface)] px-6 py-14 flex flex-col items-center text-center gap-3 ${
          over ? "border-[var(--info)]" : "border-[var(--hairline-strong)]"
        }`}
        >
          <div className="w-10 h-10 rounded-[12px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] flex items-center justify-center">
            <Upload className="w-[18px] h-[18px] text-[var(--text-tertiary)]" strokeWidth={1.5} />
          </div>
          <div>
            <div className="text-[14px] font-medium tracking-[-0.005em]">Drop a skill here</div>
            <div className="text-[12.5px] text-[var(--text-tertiary)] mt-1">A folder, a .zip or .skill file, or a single SKILL.md · up to 10 MB, or a .zip that unpacks to 20 MB</div>
          </div>
          <div className="flex flex-wrap justify-center gap-2 mt-1">
            <GhostButton onClick={() => folderInput.current?.click()}>
              <FolderUp className="w-3.5 h-3.5" strokeWidth={1.75} /> Choose a folder
            </GhostButton>
            <GhostButton onClick={() => fileInput.current?.click()}>Choose a file</GhostButton>
          </div>
          {/* webkitdirectory isn't in React's types; set as an attribute it works everywhere that matters */}
          <input ref={folderInput} type="file" className="hidden" onChange={picked} {...{ webkitdirectory: "", directory: "" }} aria-label="Choose a folder" />
          <input ref={fileInput} type="file" className="hidden" accept=".zip,.skill,.md" onChange={picked} aria-label="Choose a file" />
        </div>
      </div>
      <Anatomy />
    </>
  );
}

/** What goes in a skill folder, for someone making their first. */
function Anatomy() {
  const rows: [string, string][] = [
    ["SKILL.md", "A name and a description between two --- lines, then the instructions. The agent picks a skill by its description."],
    ["references/", "Longer reading the instructions point to; loaded only when needed."],
    ["scripts/", "Code the instructions run. It runs only on an agent whose sandbox is on."],
    ["assets/", "Templates, images and anything else the work uses."],
  ];
  return (
    <ListCard label="What a skill folder holds">
      {rows.map(([path, what]) => (
        <div key={path} className="flex items-baseline gap-4 px-4 py-2.5 min-w-0">
          <code className="w-[104px] shrink-0 font-mono text-[12px] text-[var(--text-primary)]">{path}</code>
          <span className="min-w-0 text-[12.5px] leading-[1.55] text-[var(--text-tertiary)]">{what}</span>
        </div>
      ))}
    </ListCard>
  );
}

/** Files in tree order — SKILL.md first, folders before files — with their paths. */
function ordered(files: SkillFile[]): SkillFile[] {
  const out: SkillFile[] = [];
  const visit = (nodes: ReturnType<typeof buildTree>) =>
    nodes.forEach((n) => (n.children ? visit(n.children) : n.file && out.push(n.file)));
  visit(buildTree(files));
  return out;
}

/** What the checker found: the skill it would be, every problem by its file, and SKILL.md. */
function Found({ check }: { check: SkillCheck }) {
  const byPath = (path: string | null): SkillProblem[] => check.problems.filter((p) => p.path === path);
  const whole = byPath(null);
  const listed = new Set(check.files.map((f) => f.path));
  // a problem about a path the list doesn't hold (a symlink the server dropped) still shows
  const stray = check.problems.filter((p) => p.path && !listed.has(p.path));
  const n = check.problems.length;
  return (
    <>
      <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] p-4 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[15px] font-semibold tracking-[-0.01em] font-mono break-all">{check.name ?? "No name yet"}</span>
          <Chip><span className="font-mono tabular-nums">{check.files.length}</span>&nbsp;{check.files.length === 1 ? "file" : "files"}</Chip>
          <Chip><span className="font-mono tabular-nums">{formatBytes(check.size)}</span></Chip>
          {check.needs_sandbox && (
            <span title="It ships scripts: they run only on an agent whose sandbox is on">
              <Chip><SquareTerminal className="w-3 h-3 mr-1" strokeWidth={1.75} />Needs a sandbox</Chip>
            </span>
          )}
        </div>
        <p className="m-0 text-[13px] leading-[1.6] text-[var(--text-secondary)] [overflow-wrap:anywhere]">
          {check.description ?? <span className="italic text-[var(--text-quaternary)]">No description</span>}
        </p>
        <p className="m-0 flex items-start gap-1.5 text-[12px] leading-[1.55] text-[var(--text-tertiary)]">
          <ShieldQuestion className="w-3.5 h-3.5 shrink-0 mt-[1px]" strokeWidth={1.75} />
          Not audited: skills.sh's partners audit the public catalog only. An agent's owner reads its files before saying yes.
        </p>
      </div>

      {n > 0 ? (
        <div role="alert" className="rounded-[14px] bg-[var(--danger-bg)] ring-1 ring-[var(--danger-ring)] px-4 py-3 space-y-1">
          <div className="text-[13px] font-medium text-[var(--danger)]">
            {n === 1 ? "One problem" : `${n} problems`} to fix before it can be saved
          </div>
          <ProblemLines problems={[...whole, ...stray]} withPath />
          {whole.length + stray.length < n && (
            <div className="text-[12px] text-[var(--danger)]">
              {whole.length + stray.length ? "The rest are marked on their files below." : "Each is marked on its file below."}
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-[14px] bg-[var(--success-bg)] px-4 py-3 text-[13px] text-[var(--success)]">
          Ready to save. Nothing is stored until you do.
        </div>
      )}

      <ListCard label={<Counted n={check.files.length} noun="file" />}>
        {ordered(check.files).map((f) => {
          const mine = byPath(f.path);
          return (
            <div key={f.path} className={`flex items-start gap-3 px-4 py-2.5 min-w-0 ${mine.length ? "bg-[var(--danger-bg)]" : ""}`}>
              <FileTypeIcon kind={extension(f.path)} size={28} />
              <div className="min-w-0 flex-1 py-[3px]">
                <div className="font-mono text-[12.5px] text-[var(--text-primary)] break-all">{f.path}</div>
                <ProblemLines problems={mine} />
              </div>
              <span className="shrink-0 py-[3px] font-mono tabular-nums text-[12px] text-[var(--text-tertiary)]">{formatBytes(f.size)}</span>
            </div>
          );
        })}
      </ListCard>

      {check.skill_md != null && (
        <ListCard label="SKILL.md">
          <div className="px-5 py-4 min-w-0"><SkillMarkdown text={check.skill_md} /></div>
        </ListCard>
      )}
    </>
  );
}
